import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, LogIn, UserPlus, ShoppingCart, Tv, Wrench, RefreshCw, CreditCard, Info, ChevronRight, Mail, Check, Download, PackageSearch, SearchX } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import SecureCredentialCard from './SecureCredentialCard';

// Routes RÉELLES du projet (liste blanche). Aucune URL libre générée par l'IA.
const ROUTES = {
  offers: { path: '/offre', label: 'Voir les abonnements', icon: ShoppingCart },
  login: { path: '/connexion', label: 'Se connecter', icon: LogIn },
  register: { path: '/inscription', label: 'Créer un compte', icon: UserPlus },
  account: { path: '/client', label: 'Mes abonnements', icon: Tv },
  finalize: { path: '/client', label: 'Finaliser le paiement', icon: CreditCard },
  renew: { path: '/offre', label: "Renouveler l'abonnement", icon: RefreshCw },
  tutorials: { path: '/tutoriels', label: 'Configurer mon appareil', icon: Wrench },
  downloads: { path: '/telechargements', label: 'Ouvrir Téléchargements', icon: Download },
};

const NavButton = ({ routeKey, onNavigate, primary }) => {
  const r = ROUTES[routeKey];
  if (!r) return null;
  const Icon = r.icon;
  const handle = () => {
    if (routeKey === 'downloads') {
      sessionStorage.setItem('nova_downloads_source', 'nova');
      api.post('/assistant/page-event', { event: 'downloads_opened', source: 'nova' }).catch(() => {});
    }
    onNavigate(r.path);
  };
  return (
    <button
      onClick={handle}
      className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg transition-all ${
        primary
          ? 'bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white hover:shadow-lg hover:shadow-[#FF6B35]/30'
          : 'bg-[#1A1A1D] border border-[#3A3A3D] text-[#E8E8E8] hover:border-[#FF6B35] hover:text-[#FF6B35]'
      }`}
      data-testid={`nova-nav-${routeKey}`}
    >
      <Icon className="h-3.5 w-3.5" /> {r.label}
    </button>
  );
};

const InfoBox = ({ children, testid }) => (
  <div className="mt-3 rounded-xl border border-[#00FFFF]/25 bg-[#00FFFF]/5 p-3 text-sm text-[#E8E8E8]" data-testid={testid}>
    <div className="flex items-start gap-2">
      <Info className="h-4 w-4 text-[#00FFFF] flex-shrink-0 mt-0.5" />
      <div className="flex-1">{children}</div>
    </div>
  </div>
);

const SubStatusRow = ({ sub, onNavigate, selectable, onSelect }) => {
  const ctaByStatus = {
    expired: 'renew',
    pending_payment: 'finalize',
  };
  const cta = ctaByStatus[sub.status];
  const statusColor = {
    active: 'text-[#00FF88] bg-[#00FF88]/15',
    expired: 'text-[#FF3B5C] bg-[#FF3B5C]/15',
    pending_payment: 'text-[#FFB800] bg-[#FFB800]/15',
    pending_credentials: 'text-[#FFB800] bg-[#FFB800]/15',
  }[sub.status] || 'text-[#B8B8BA] bg-[#1A1A1D]';

  const inner = (
    <>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white font-medium">{sub.plan_label}</p>
        <div className="flex flex-wrap items-center gap-2 mt-0.5">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statusColor}`}>{sub.status_label}</span>
          {sub.end_date && (
            <span className="text-[10px] text-[#6B6B6E]">
              {sub.days_remaining != null && sub.days_remaining >= 0
                ? `Expire dans ${sub.days_remaining} j`
                : `Expiré le ${new Date(sub.end_date).toLocaleDateString('fr-FR')}`}
            </span>
          )}
        </div>
      </div>
      {selectable && <ChevronRight className="h-4 w-4 text-[#FF6B35] flex-shrink-0" />}
    </>
  );

  if (selectable) {
    return (
      <button
        onClick={() => onSelect(sub)}
        className="w-full flex items-center gap-2 rounded-lg bg-[#0A0A0B] border border-[#3A3A3D] px-3 py-2.5 text-left hover:border-[#FF6B35] transition-colors"
        data-testid="nova-sub-select"
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="rounded-lg bg-[#0A0A0B] border border-[#3A3A3D] px-3 py-2.5" data-testid="nova-sub-row">
      <div className="flex items-center gap-2">{inner}</div>
      {cta && <div className="mt-2"><NavButton routeKey={cta} onNavigate={onNavigate} primary /></div>}
    </div>
  );
};

// Gère l'ensemble des parcours déclenchés par une action de Nova.
export const NovaActionZone = ({ action, isLoggedIn, onNavigate }) => {
  const type = action?.type;
  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState(null);
  const [profile, setProfile] = useState(null);
  const [credential, setCredential] = useState(null);
  const [credLabel, setCredLabel] = useState('');
  const [credLoading, setCredLoading] = useState(false);
  const [guideSent, setGuideSent] = useState(false);
  const [renewInfo, setRenewInfo] = useState(null); // {sub, amount, payment_url}
  const [renewLoading, setRenewLoading] = useState(false);
  const [appReported, setAppReported] = useState(false);

  const needsAccount = type === 'show_credentials' || type === 'configure_device' || type === 'check_my_subscription';
  const isRenew = type === 'renew_subscription';

  const fetchProfile = useCallback(async (app, device) => {
    if (!app && !device) return;
    try {
      const r = await api.get('/assistant/connection-profiles/resolve', { params: { app, device } });
      if (r.data && r.data.profile) setProfile(r.data.profile);
    } catch (e) { /* profil non bloquant */ }
  }, []);

  const sendGuide = useCallback(async () => {
    try {
      await api.post('/assistant/send-guide', { app: action?.app, device: action?.device });
      setGuideSent(true);
      toast.success('Guide envoyé par e-mail (sans vos identifiants).');
    } catch (e) { toast.error("Envoi impossible."); }
  }, [action]);

  const prepareRenew = useCallback(async (sub) => {
    setRenewLoading(true);
    try {
      const r = await api.post(`/client/renew-subscription/${sub.subscription_id}`);
      setRenewInfo({ sub, amount: r.data.amount, payment_url: r.data.payment_url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Renouvellement indisponible pour cet abonnement.");
    } finally { setRenewLoading(false); }
  }, []);

  const fetchCredential = useCallback(async (sub) => {
    setCredLoading(true);
    try {
      const r = await api.get('/iptv-credentials/my', { params: { subscription_id: sub.subscription_id } });
      const cred = (r.data || [])[0];
      setCredential(cred || null);
      setCredLabel(cred ? `${sub.plan_label} · ${sub.status_label}` : '');
    } catch (e) {
      setCredential(null);
    } finally {
      setCredLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setSubs(null); setProfile(null); setCredential(null); setCredLabel(''); setGuideSent(false); setRenewInfo(null);
    if (action?.app || action?.device) fetchProfile(action.app, action.device);
    if ((needsAccount || isRenew) && isLoggedIn) {
      setLoading(true);
      api.get('/assistant/my-subscriptions')
        .then((r) => { if (!cancelled) setSubs(r.data.items || []); })
        .catch(() => { if (!cancelled) setSubs([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, isLoggedIn]);

  // Auto-sélection de l'unique abonnement disposant d'identifiants
  useEffect(() => {
    if (!subs || type === 'check_my_subscription') return;
    const withCreds = subs.filter((s) => s.has_credentials);
    if (withCreds.length === 1 && !credential && !credLoading) {
      fetchCredential(withCreds[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subs]);

  if (!type) return null;

  // --- Installation via l'onglet Téléchargements (Phase 4.4-B) ---
  if (type === 'open_downloads_page') {
    const dev = (action?.device || '').toLowerCase();
    const isFireTv = /fire\s?tv|fire\s?stick|firestick|fire os/.test(dev);
    const reportMissing = async () => {
      try {
        await api.post('/assistant/app-missing-from-downloads', {
          application_name: action?.app || null, device: action?.device || null,
        });
        setAppReported(true);
        toast.success("Merci, nous avons noté votre demande.");
      } catch (e) { toast.error("Impossible d'enregistrer la demande."); }
    };
    return (
      <div className="mt-3 rounded-xl border border-[#FF6B35]/30 bg-[#FF6B35]/5 p-3 space-y-2.5" data-testid="nova-install-card">
        <div className="flex items-center gap-2">
          <PackageSearch className="h-4 w-4 text-[#FF6B35]" />
          <p className="text-sm text-white font-semibold">
            Installation{action?.app ? ` de ${action.app}` : ''}{action?.device ? ` sur ${action.device}` : ''}
          </p>
        </div>
        {isFireTv ? (
          <p className="text-xs text-[#B8B8BA]">
            Sur Fire TV, nous utilisons l'application <strong className="text-[#E8E8E8]">Downloader</strong>. Ouvrez l'onglet
            Téléchargements pour récupérer le code correspondant à l'application, puis saisissez-le dans Downloader.
          </p>
        ) : (
          <p className="text-xs text-[#B8B8BA]">
            Retrouvez dans l'onglet Téléchargements les applications disponibles et les codes à utiliser.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <NavButton routeKey="downloads" onNavigate={onNavigate} primary />
          <NavButton routeKey="tutorials" onNavigate={onNavigate} />
        </div>
        {!appReported ? (
          <button onClick={reportMissing} className="flex items-center gap-1.5 text-[11px] text-[#6B6B6E] hover:text-[#FF3B5C] transition-colors" data-testid="nova-app-missing-btn">
            <SearchX className="h-3.5 w-3.5" /> Je ne trouve pas l'application dans Téléchargements
          </button>
        ) : (
          <p className="text-[11px] text-[#00FF88]">Demande enregistrée. Nova peut vous proposer une autre application compatible.</p>
        )}
        <p className="text-[10px] text-[#6B6B6E]">🔒 Le code s'affiche dans l'onglet Téléchargements : récupérez-le vous-même, Nova ne le communique pas ici.</p>
      </div>
    );
  }

  // --- Souscription (visiteur ou client) ---
  if (type === 'start_subscription') {
    return (
      <div className="mt-3 flex flex-wrap gap-2" data-testid="nova-action-subscribe">
        <NavButton routeKey="offers" onNavigate={onNavigate} primary />
        {isLoggedIn
          ? <NavButton routeKey="account" onNavigate={onNavigate} />
          : (<><NavButton routeKey="register" onNavigate={onNavigate} /><NavButton routeKey="login" onNavigate={onNavigate} /></>)}
      </div>
    );
  }

  // --- Non connecté : identifiants / config / statut / renouvellement ---
  if ((needsAccount || isRenew) && !isLoggedIn) {
    return (
      <InfoBox testid="nova-login-required">
        <p className="mb-2">Pour accéder à vos identifiants, vous devez vous connecter à votre compte.</p>
        <div className="flex flex-wrap gap-2">
          <NavButton routeKey="login" onNavigate={onNavigate} primary />
          <NavButton routeKey="register" onNavigate={onNavigate} />
          <NavButton routeKey="offers" onNavigate={onNavigate} />
        </div>
      </InfoBox>
    );
  }

  if (loading) {
    return (
      <div className="mt-3 flex items-center gap-2 text-[#B8B8BA] text-xs" data-testid="nova-action-loading">
        <Loader2 className="h-3.5 w-3.5 text-[#FF6B35] animate-spin" /> Vérification de vos abonnements…
      </div>
    );
  }

  if (subs && subs.length === 0) {
    return (
      <InfoBox testid="nova-no-subscription">
        <p className="mb-2">Vous ne disposez actuellement d'aucun abonnement actif.</p>
        <NavButton routeKey="offers" onNavigate={onNavigate} primary />
      </InfoBox>
    );
  }

  // --- Renouvellement express ---
  if (isRenew && subs) {
    if (renewInfo) {
      return (
        <div className="mt-3 rounded-xl border border-[#FF6B35]/40 bg-[#FF6B35]/5 p-3" data-testid="nova-renew-confirm">
          <p className="text-sm text-white font-semibold mb-1">Confirmation du renouvellement</p>
          <p className="text-xs text-[#B8B8BA] mb-1">{renewInfo.sub.plan_label}</p>
          <p className="text-sm text-[#E8E8E8] mb-3">Montant à régler : <strong className="text-[#FF6B35]">{renewInfo.amount} €</strong></p>
          <p className="text-[11px] text-[#6B6B6E] mb-3">Aucun paiement n'est déclenché automatiquement. Vous serez redirigé vers la page de paiement sécurisée après confirmation.</p>
          <div className="flex gap-2">
            <button onClick={() => onNavigate(renewInfo.payment_url)} className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-medium" data-testid="nova-renew-confirm-btn">
              <Check className="h-3.5 w-3.5" /> Confirmer et payer
            </button>
            <button onClick={() => setRenewInfo(null)} className="text-xs px-3 py-2 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#B8B8BA]" data-testid="nova-renew-cancel-btn">Annuler</button>
          </div>
        </div>
      );
    }
    const renewable = subs.filter((s) => s.status === 'expired' || (s.days_remaining != null && s.days_remaining <= 30));
    if (renewable.length === 0) {
      return (
        <div className="mt-3 space-y-2" data-testid="nova-renew-none">
          <InfoBox><p>Aucun de vos abonnements n'est éligible au renouvellement pour le moment (renouvellement possible à moins de 30 jours de l'expiration).</p></InfoBox>
          {subs.map((s) => <SubStatusRow key={s.subscription_id} sub={s} onNavigate={onNavigate} />)}
        </div>
      );
    }
    return (
      <div className="mt-3 space-y-2" data-testid="nova-renew-list">
        <p className="text-sm text-[#E8E8E8]">Quel abonnement souhaitez-vous renouveler ?</p>
        {renewLoading && <div className="flex items-center gap-2 text-[#B8B8BA] text-xs"><Loader2 className="h-3.5 w-3.5 text-[#FF6B35] animate-spin" /> Préparation…</div>}
        {renewable.map((s) => (
          <div key={s.subscription_id} className="rounded-lg bg-[#0A0A0B] border border-[#3A3A3D] px-3 py-2.5" data-testid="nova-renew-row">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm text-white font-medium">{s.plan_label}</p>
                <span className="text-[11px] text-[#B8B8BA]">{s.status_label}</span>
              </div>
              <button onClick={() => prepareRenew(s)} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white" data-testid="nova-renew-prepare-btn">
                <RefreshCw className="h-3.5 w-3.5" /> Renouveler mon abonnement
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // --- Vérification de statut (aucune donnée sensible) ---
  if (type === 'check_my_subscription' && subs) {
    return (
      <div className="mt-3 space-y-2" data-testid="nova-subscription-status">
        <p className="text-[11px] text-[#6B6B6E]">Vos abonnements :</p>
        {subs.map((s) => <SubStatusRow key={s.subscription_id} sub={s} onNavigate={onNavigate} />)}
      </div>
    );
  }

  // --- Identifiants / configuration ---
  if (subs) {
    const withCreds = subs.filter((s) => s.has_credentials);

    if (withCreds.length === 0) {
      // Aucun identifiant encore disponible → expliquer le statut réel
      return (
        <div className="mt-3 space-y-2" data-testid="nova-credentials-unavailable">
          <InfoBox>
            <p>Vos identifiants ne sont pas encore disponibles pour le moment. Voici l'état de vos abonnements :</p>
          </InfoBox>
          {subs.map((s) => <SubStatusRow key={s.subscription_id} sub={s} onNavigate={onNavigate} />)}
        </div>
      );
    }

    if (withCreds.length > 1 && !credential) {
      return (
        <div className="mt-3 space-y-2" data-testid="nova-credentials-choose">
          <p className="text-sm text-[#E8E8E8]">Vous disposez de plusieurs abonnements. Lequel souhaitez-vous configurer ?</p>
          {credLoading
            ? <div className="flex items-center gap-2 text-[#B8B8BA] text-xs"><Loader2 className="h-3.5 w-3.5 text-[#FF6B35] animate-spin" /> Chargement…</div>
            : withCreds.map((s) => (
                <SubStatusRow key={s.subscription_id} sub={s} selectable onSelect={fetchCredential} onNavigate={onNavigate} />
              ))}
        </div>
      );
    }

    if (credLoading) {
      return (
        <div className="mt-3 flex items-center gap-2 text-[#B8B8BA] text-xs">
          <Loader2 className="h-3.5 w-3.5 text-[#FF6B35] animate-spin" /> Récupération sécurisée de vos identifiants…
        </div>
      );
    }

    if (credential) {
      return (
        <div>
          <SecureCredentialCard credential={credential} profile={profile} subLabel={credLabel} />
          <button
            onClick={sendGuide}
            disabled={guideSent}
            className="mt-2 flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#E8E8E8] hover:border-[#00FFFF] hover:text-[#00FFFF] disabled:opacity-60"
            data-testid="nova-send-guide-btn"
          >
            {guideSent ? <Check className="h-3.5 w-3.5 text-[#00FF88]" /> : <Mail className="h-3.5 w-3.5" />}
            {guideSent ? 'Guide envoyé par e-mail' : 'Recevoir le guide par e-mail'}
          </button>
        </div>
      );
    }
  }

  return null;
};

export default NovaActionZone;
